import { Component, OnInit } from '@angular/core';
import { User } from './user';
import { Car } from './car';
import { RegistrationService } from './registration.service';
import { NotificationService } from './notifications/notification.service';
import { AuthService } from './auth.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {
  title = 'upcoming-cars';
  
  user: User = new User();
  newCar: Car = new Car();
  newUser: User = new User();
  isLoggedIn = false;
  msg: string = '';
  successMsg: string = '';
  cars: Car[] = [];
  notifications: any[] = [];
  currentUser: User = new User();
  editProfileMode = false;
  usersList: User[] = [];
  isLoading = false;

  constructor(private registrationService: RegistrationService, private notificationService: NotificationService, private authService: AuthService) {}

  ngOnInit(): void {
    // Check if user is already logged in (token stored by login)
    const token = sessionStorage.getItem('token1');
    if (token) {
      this.authService.setLoggedIn(true);
      this.loadCars();
    }

    // Subscribe to auth state for navbar visibility
    this.authService.isLoggedIn$.subscribe(v => {
      this.isLoggedIn = v;
    });

    // Subscribe to shared notifications so navbar badge always reflects latest state
    this.notificationService.notifications$.subscribe(arr => {
      this.notifications = arr;
    });
  }

  doLogin(): void {
    if (!this.user.username || !this.user.password) {
      this.msg = 'Please enter username and password';
      return;
    }
    
    this.isLoading = true;
    this.registrationService.login(this.user).subscribe(
      (response: any) => {
        this.isLoggedIn = true;
        this.msg = '';
        this.successMsg = 'Welcome! You are now logged in.';
        this.loadCars();
        this.isLoading = false;
        setTimeout(() => this.successMsg = '', 4000);
      },
      (error: any) => {
        this.msg = 'Login failed. Please check your credentials.';
        this.isLoading = false;
        console.error('Login error:', error);
      }
    );
  }

  logout(): void {
    sessionStorage.removeItem('token1');
    this.authService.setLoggedIn(false);
    this.user = new User();
    this.cars = [];
    this.notifications = [];
    this.currentUser = new User();
    this.editProfileMode = false;
    this.successMsg = 'You have been logged out.';
    setTimeout(() => this.successMsg = '', 3000);
  }

  loadCars(): void {
    this.isLoading = true;
    this.registrationService.CarListFromRemote().subscribe(
      (cars: Car[]) => {
        this.cars = cars;
        this.isLoading = false;
      },
      (error: any) => {
        console.error('Error loading cars:', error);
        this.isLoading = false;
      }
    );
  }

  onAddCarSubmit(): void {
    // Validate required fields
    if (!this.newCar.brand || !this.newCar.model || !this.newCar.year || !this.newCar.price) {
      this.msg = 'Please fill in all required fields (Brand, Model, Year, Price)';
      return;
    }

    // Validate year
    const currentYear = new Date().getFullYear();
    if (this.newCar.year < 1900 || this.newCar.year > currentYear + 10) {
      this.msg = 'Please enter a valid year';
      return;
    }

    // Validate price
    if (this.newCar.price <= 0) {
      this.msg = 'Price must be greater than 0';
      return;
    }
    
    this.msg = '';
    this.isLoading = true;
    
    if (this.newCar.carId) {
      // Update existing car
      this.registrationService.updateCar(this.newCar.carId, this.newCar).subscribe(
        (response: any) => {
          this.successMsg = 'Car updated successfully!';
          this.newCar = new Car();
          this.loadCars();
          this.isLoading = false;
          setTimeout(() => this.successMsg = '', 4000);
        },
        (error: any) => {
          this.msg = 'Failed to update car. Please try again.';
          this.isLoading = false;
          console.error('Error updating car:', error);
        }
      );
    } else {
      // Add new car
      this.registrationService.addCarFromRemote(this.newCar).subscribe(
        (response: any) => {
          this.successMsg = 'Car added successfully!';
          this.newCar = new Car();
          this.loadCars();
          this.isLoading = false;
          setTimeout(() => this.successMsg = '', 4000);
        },
        (error: any) => {
          this.msg = 'Failed to add car. Please try again.';
          this.isLoading = false;
          console.error('Error adding car:', error);
        }
      );
    }
  }

  updateProfile(): void {
    // Validate required fields
    if (!this.currentUser.username || !this.currentUser.email) {
      this.msg = 'Username and email are required';
      return;
    }

    // Validate email format
    if (!this.validateEmail(this.currentUser.email)) {
      this.msg = 'Please enter a valid email address';
      return;
    }

    // Validate phone if provided
    if (this.currentUser.phone && !this.validatePhone(this.currentUser.phone)) {
      this.msg = 'Please enter a valid phone number';
      return;
    }

    this.msg = '';
    this.isLoading = true;
    
    // Simulate API call for profile update
    setTimeout(() => {
      this.successMsg = 'Profile updated successfully!';
      this.editProfileMode = false;
      this.isLoading = false;
      setTimeout(() => this.successMsg = '', 4000);
    }, 800);
  }

  addNewUser(): void {
    // Validate required fields
    if (!this.newUser.username || !this.newUser.email || !this.newUser.password) {
      this.msg = 'Please fill in all required fields (Username, Email, Password)';
      return;
    }

    // Validate username length
    if (this.newUser.username.length < 3) {
      this.msg = 'Username must be at least 3 characters long';
      return;
    }

    // Validate email format
    if (!this.validateEmail(this.newUser.email)) {
      this.msg = 'Please enter a valid email address';
      return;
    }

    // Validate password strength
    if (!this.validatePasswordStrength(this.newUser.password)) {
      this.msg = 'Password must be at least 6 characters with letters and numbers';
      return;
    }

    // Validate phone if provided
    if (this.newUser.phone && !this.validatePhone(this.newUser.phone)) {
      this.msg = 'Please enter a valid phone number';
      return;
    }

    this.msg = '';
    this.isLoading = true;
    
    // Check if user already exists
    const userExists = this.usersList.some(u => u.username === this.newUser.username || u.email === this.newUser.email);
    if (userExists) {
      this.msg = 'Username or email already exists';
      this.isLoading = false;
      return;
    }

    // Add user with default values if not provided
    const newUser = {
      ...this.newUser,
      userId: this.usersList.length + 1,
      firstName: this.newUser.firstName || 'User',
      lastName: this.newUser.lastName || '',
      role: this.newUser.role || 'user',
      phone: this.newUser.phone || ''
    };

    this.usersList.push(newUser);
    this.successMsg = `User "${this.newUser.username}" created successfully!`;
    this.newUser = new User();
    this.isLoading = false;
    setTimeout(() => this.successMsg = '', 4000);
  }

  connectWebSocket(): void {
    this.isLoading = true;
    setTimeout(() => {
      this.successMsg = 'WebSocket connected successfully!';
      this.isLoading = false;
      setTimeout(() => this.successMsg = '', 4000);
    }, 1000);
  }

  disconnectWebSocket(): void {
    this.successMsg = 'WebSocket disconnected.';
    setTimeout(() => this.successMsg = '', 3000);
  }

  toggleEditProfile(): void {
    this.editProfileMode = !this.editProfileMode;
  }

  editUser(index: number): void {
    if (index >= 0 && index < this.usersList.length) {
      const user = this.usersList[index];
      // Pre-fill the form with user data
      this.newUser = { ...user };
      // Remove from list, will be re-added with updated data
      this.usersList.splice(index, 1);
      this.successMsg = `Editing user: ${user.username}`;
      setTimeout(() => this.successMsg = '', 3000);
    }
  }

  deleteUser(index: number): void {
    if (index >= 0 && index < this.usersList.length) {
      const user = this.usersList[index];
      if (confirm(`Are you sure you want to delete user "${user.username}"?`)) {
        this.isLoading = true;
        // Simulate API call for delete
        setTimeout(() => {
          this.usersList.splice(index, 1);
          this.successMsg = `User "${user.username}" deleted successfully!`;
          this.isLoading = false;
          setTimeout(() => this.successMsg = '', 3000);
        }, 500);
      }
    }
  }

  deleteCar(carId: number): void {
    const car = this.cars.find(c => c.carId === carId);
    if (car && confirm(`Are you sure you want to delete ${car.brand} ${car.model}?`)) {
      this.isLoading = true;
      this.registrationService.deleteCar(carId).subscribe(
        (response: any) => {
          this.cars = this.cars.filter(c => c.carId !== carId);
          this.successMsg = `Car deleted successfully!`;
          this.isLoading = false;
          setTimeout(() => this.successMsg = '', 3000);
        },
        (error: any) => {
          this.msg = 'Failed to delete car. Please try again.';
          this.isLoading = false;
          console.error('Error deleting car:', error);
        }
      );
    }
  }

  editCar(car: Car): void {
    // Implement edit car functionality
    this.newCar = { ...car };
    // Scroll to add car form
    const element = document.getElementById('add-car');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    this.successMsg = `Editing ${car.brand} ${car.model}`;
    setTimeout(() => this.successMsg = '', 3000);
  }

  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  validatePhone(phone: string): boolean {
    const phoneRegex = /^[\d\s\-\(\)\+]{10,}$/;
    return phoneRegex.test(phone);
  }

  validatePasswordStrength(password: string): boolean {
    // Minimum 6 characters, at least one number and one letter
    return password.length >= 6 && /\d/.test(password) && /[a-zA-Z]/.test(password);
  }

  cancelEditCar(): void {
    this.newCar = new Car();
  }

  cancelEditUser(): void {
    this.newUser = new User();
  }
}
